import type { TaskComment } from '@frontend/api-types';
import { dateFormatters } from '@frontend/duck-tape';
import { Text, YStack } from '@frontend/web-react';
import { TaskChatWhisper } from '@frontend/web/components';
import { useState } from 'react';
import { WhisperActivityToggle } from './WhisperActivityToggle';

type WhisperGroupProps = {
  comments: TaskComment[];
  contentType?: 'html' | 'markdown';
  taskId?: string;
};

export const WhisperGroup = ({ comments, contentType = 'html', taskId }: WhisperGroupProps) => {
  const [isActivityVisible, setIsActivityVisible] = useState(false);

  // Determine the number of middle whispers
  const middleWhisperCount = comments.length - 3;

  // Toggle the visibility of middle whispers
  const toggleActivity = () => setIsActivityVisible(!isActivityVisible);

  return (
    <YStack className="px-xl gap-xs">
      {/* Always render the first 2 whispers */}
      <YStack className="gap-sm">
        <Text className="text-neutral-400" type="p-sm">
          {comments[0]?.createdAt ? dateFormatters.friendlyDate(comments[0]?.createdAt) : ''}
        </Text>
        {comments.slice(0, 2).map((comment, index) => (
          <TaskChatWhisper
            body={comment.body}
            commentId={comment.id}
            contentType={contentType}
            createdAt={comment.createdAt}
            isLineDotted={comments.length > 3 && !isActivityVisible && index + 1 === 2}
            isLineVisible={index + 1 !== comments.length}
            key={comment.id}
            taskId={taskId}
            whisper={comment.whisper}
          />
        ))}
      </YStack>

      {/* Show toggle button if there are middle whispers */}
      {middleWhisperCount > 0 ? (
        <>
          {!isActivityVisible && (
            <WhisperActivityToggle
              activityCount={middleWhisperCount}
              isActive={isActivityVisible}
              onToggleActivity={toggleActivity}
            />
          )}
          {/* Render middle whispers based on toggle state */}
          {isActivityVisible
            ? comments
                .slice(2, comments.length - 1)
                .map((comment, index) => (
                  <TaskChatWhisper
                    body={comment.body}
                    commentId={comment.id}
                    contentType={contentType}
                    createdAt={comment.createdAt}
                    isLineDotted={index + 1 >= comments.slice(2, comments.length - 1).length}
                    isLineVisible
                    key={comment.id}
                    taskId={taskId}
                    whisper={comment.whisper}
                  />
                ))
            : null}
          {isActivityVisible ? (
            <WhisperActivityToggle
              activityCount={middleWhisperCount}
              isActive={isActivityVisible}
              onToggleActivity={toggleActivity}
            />
          ) : null}
        </>
      ) : null}
      {/* Always render the last whisper */}
      {comments.length >= 3 ? (
        <TaskChatWhisper
          body={comments[comments.length - 1]?.body as string}
          commentId={comments[comments.length - 1]?.id}
          contentType={contentType}
          createdAt={comments[comments.length - 1]?.createdAt as DateTimeISOString}
          isLineVisible={false}
          taskId={taskId}
          whisper={comments[comments.length - 1]?.whisper}
        />
      ) : null}
    </YStack>
  );
};
